// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    variant = "variant",
    sizeVariant = "sizeVariant",
    label = "label",
    link = "link",
    alignment = "alignment",
    openInNewTab = "openInNewTab",
    openInModal = "openInModal",
    buttonId = "buttonId",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.sizeVariant]: "" | "small" | "medium" | "large";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.openInNewTab]: boolean;
        [fields.openInModal]: boolean;
        [fields.buttonId]: string;
        [fields.customCSS]: string;
    };
}

export interface ButtonStyles {
    wrapperStyles?: InjectableCss;
    imageModal?: ModalPresentationProps;
}

export const buttonStyles: ButtonStyles = {
    imageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 40px 10px 0;
            `,
            modalContent: css`
                text-align: center;
                padding: 20px;
            `,
        },
    },
};

const wrapperStyles: InjectableCss = {};

export const CmsButton: FC<OwnProps> = ({ id, fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const closeModalHandler = () => {
        setModalIsOpen(false);
    };

    const bStyles = useMergeStyles("button", { wrapperStyles });
    const [styles] = React.useState(() => mergeToNew(bStyles, buttonStyles));

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <ContentWrapper alignment={fields.alignment} {...styles?.wrapperStyles} className="wrapper">
                <Button
                    variant={fields.variant}
                    {...(fields.sizeVariant && { sizeVariant: fields.sizeVariant })}
                    className="button"
                    {...({
                        href: link.url && !fields.openInModal ? link.url : "",
                        className: "link-added",
                    } as any)}
                    target={fields.openInNewTab ? "_blank" : ""}
                    onClick={() => {
                        if (fields.openInModal) {
                            setModalIsOpen(true);
                        }
                    }}
                    {...(fields.buttonId && { id: `${fields.buttonId}_${id}` })}
                >
                    {fields.label}
                </Button>
            </ContentWrapper>
            <Modal {...styles.imageModal} isOpen={modalIsOpen} handleClose={closeModalHandler}>
                <Zone contentId={id} zoneName="Content" />
            </Modal>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: CmsButton,
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.sizeVariant,
                displayName: "Button Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "medium",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.openInModal,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.buttonId,
                displayName: "Button Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
